import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import { galleryAPI } from '../../services/api';
import { getStorageUrl } from '../../utils/imageHelpers';
import { 
  ArrowLeft, Plus, Image as ImageIcon, Video, Trash2, Upload, X, Edit
} from 'lucide-react';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const AlbumInfo = styled(Card)`
  padding: 1.5rem;
  background: ${props => props.theme.colors.background.secondary};
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const MediaCard = styled(Card)`
  padding: 0;
  overflow: hidden;
  position: relative;
  
  &:hover .media-actions {
    opacity: 1;
  }
`;

const MediaPreview = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: ${props => props.theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  img, iframe {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const MediaActions = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
`;

const MediaInfo = styled.div`
  padding: 1rem;
  
  h4 {
    margin: 0 0 0.5rem 0;
    font-size: ${props => props.theme.typography.fontSize.sm};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    color: ${props => props.theme.colors.text.primary};
  }
  
  p {
    margin: 0;
    font-size: ${props => props.theme.typography.fontSize.xs};
    color: ${props => props.theme.colors.text.secondary};
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const UploadArea = styled.div`
  border: 2px dashed ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.theme.colors.background.secondary};
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.primary}10;
  }
  
  input[type="file"] {
    display: none;
  }
`;

const AlbumMediaManagement = () => {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  const [album, setAlbum] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [imageFiles, setImageFiles] = useState([]);
  const [videoData, setVideoData] = useState({
    video_url: '',
    title: '',
    description: ''
  });

  useEffect(() => {
    fetchAlbumData();
  }, [albumId]);

  const fetchAlbumData = async () => {
    try {
      setLoading(true);
      const [albumRes, mediaRes] = await Promise.all([
        galleryAPI.getAlbum(albumId),
        galleryAPI.getMedia(albumId)
      ]);
      setAlbum(albumRes.data);
      setMedia(mediaRes.data || []);
    } catch (error) {
      console.error('Error fetching album data:', error);
      alert('Failed to load album');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    if (imageFiles.length === 0) return;

    try {
      setUploading(true);
      const formData = new FormData();
      
      if (imageFiles.length === 1) {
        formData.append('image', imageFiles[0]);
        await galleryAPI.uploadImage(albumId, formData);
      } else {
        imageFiles.forEach(file => {
          formData.append('images[]', file);
        });
        await galleryAPI.uploadMultipleImages(albumId, formData);
      }
      
      setShowImageModal(false);
      setImageFiles([]);
      fetchAlbumData();
      alert(`${imageFiles.length} image(s) uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleVideoAdd = async (e) => {
    e.preventDefault();
    if (!videoData.video_url) return;

    try {
      setUploading(true);
      await galleryAPI.addMedia(albumId, {
        media_type: 'video',
        ...videoData
      });
      
      setShowVideoModal(false);
      setVideoData({ video_url: '', title: '', description: '' });
      fetchAlbumData();
      alert('Video added successfully!');
    } catch (error) {
      console.error('Error adding video:', error);
      alert('Failed to add video');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (window.confirm('Are you sure you want to delete this media?')) {
      try {
        await galleryAPI.deleteMedia(mediaId);
        fetchAlbumData();
        alert('Media deleted successfully!');
      } catch (error) {
        console.error('Error deleting media:', error);
        alert('Failed to delete media');
      }
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(files);
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading album...</div>;
  }

  if (!album) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Album not found</div>;
  }

  return (
    <Container theme={theme}>
      <Header>
        <HeaderLeft>
          <Button variant="outline" size="small" onClick={() => navigate('/app/gallery')}>
            <ArrowLeft size={20} /> Back
          </Button>
          <Title theme={theme}>{album.title}</Title>
        </HeaderLeft>
        
        <Actions>
          <Button variant="primary" onClick={() => setShowImageModal(true)}>
            <ImageIcon size={20} /> Add Images
          </Button>
          <Button variant="secondary" onClick={() => setShowVideoModal(true)}>
            <Video size={20} /> Add Video
          </Button>
        </Actions>
      </Header>

      <AlbumInfo theme={theme}>
        <p><strong>Description:</strong> {album.description || 'No description'}</p>
        <p><strong>Location:</strong> {album.location || 'Not specified'}</p>
        <p><strong>Date:</strong> {album.event_date ? new Date(album.event_date).toLocaleDateString() : 'Not specified'}</p>
        <p><strong>Status:</strong> {album.status}</p>
        <p><strong>Total Media:</strong> {media.length} ({album.images_count || 0} photos, {album.videos_count || 0} videos)</p>
      </AlbumInfo>

      {media.length === 0 ? (
        <Card style={{ padding: '4rem 2rem', textAlign: 'center' }}>
          <ImageIcon size={80} style={{ opacity: 0.2, marginBottom: '1.5rem', color: theme.colors.primary }} />
          <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.5rem', color: theme.colors.text.primary }}>
            No Photos or Videos Yet
          </h2>
          <p style={{ margin: '0 0 2rem 0', color: theme.colors.text.secondary, maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>
            This album is empty. Click the buttons above to start adding your photos and videos!
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="primary" size="large" onClick={() => setShowImageModal(true)}>
              <ImageIcon size={20} /> Upload Images
            </Button>
            <Button variant="secondary" size="large" onClick={() => setShowVideoModal(true)}>
              <Video size={20} /> Add Video from YouTube/Vimeo
            </Button>
          </div>
        </Card>
      ) : (
        <MediaGrid>
          {media.map((item) => (
            <MediaCard key={item.id} theme={theme}>
              <MediaPreview theme={theme}>
                {item.media_type === 'image' ? (
                  <img src={getStorageUrl(item.file_path)} alt={item.title || 'Gallery image'} />
                ) : (
                  <iframe
                    src={item.video_url?.replace('watch?v=', 'embed/')}
                    title={item.title || 'Gallery video'}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
                <MediaActions className="media-actions">
                  <Button variant="outline" size="small" onClick={() => handleDeleteMedia(item.id)} style={{ color: 'white', borderColor: 'white' }}>
                    <Trash2 size={16} />
                  </Button>
                </MediaActions>
              </MediaPreview>
              
              {(item.title || item.description) && (
                <MediaInfo theme={theme}>
                  {item.title && <h4>{item.title}</h4>}
                  {item.description && <p>{item.description}</p>}
                </MediaInfo>
              )}
            </MediaCard>
          ))}
        </MediaGrid>
      )}

      {/* Image Upload Modal */}
      {showImageModal && (
        <Modal isOpen={showImageModal} onClose={() => { setShowImageModal(false); setImageFiles([]); }} title="Add Images">
          <form onSubmit={handleImageUpload}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <UploadArea theme={theme}>
                <input
                  type="file"
                  id="image-upload"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                />
                <label htmlFor="image-upload" style={{ cursor: 'pointer' }}>
                  <Upload size={48} style={{ color: theme.colors.primary, marginBottom: '1rem' }} />
                  <p style={{ margin: '0.5rem 0', fontSize: '1rem', fontWeight: 600 }}>
                    Click to select images
                  </p>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: theme.colors.text.secondary }}>
                    You can select multiple images (JPEG, PNG, GIF, WebP)
                  </p>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: theme.colors.text.secondary }}>
                    Max 10MB per image, up to 20 images at once
                  </p>
                </label>
              </UploadArea>

              {imageFiles.length > 0 && (
                <div style={{ padding: '1rem', background: theme.colors.background.secondary, borderRadius: '0.5rem' }}>
                  <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>
                    Selected: {imageFiles.length} image(s)
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {imageFiles.map((file, index) => (
                      <div key={index} style={{ fontSize: '0.875rem', color: theme.colors.text.secondary }}>
                        • {file.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <Button type="button" variant="outline" onClick={() => { setShowImageModal(false); setImageFiles([]); }}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={imageFiles.length === 0 || uploading}>
                  {uploading ? 'Uploading...' : `Upload ${imageFiles.length} Image(s)`}
                </Button>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Video Add Modal */}
      {showVideoModal && (
        <Modal isOpen={showVideoModal} onClose={() => { setShowVideoModal(false); setVideoData({ video_url: '', title: '', description: '' }); }} title="Add Video">
          <form onSubmit={handleVideoAdd}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Video URL * (YouTube or Vimeo)</Label>
                <Input 
                  value={videoData.video_url}
                  onChange={(e) => setVideoData({...videoData, video_url: e.target.value})}
                  placeholder="https://www.youtube.com/watch?v=..."
                  required
                />
                <small style={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>
                  Example: https://www.youtube.com/watch?v=VIDEO_ID or https://vimeo.com/VIDEO_ID
                </small>
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Video Title (Optional)</Label>
                <Input 
                  value={videoData.title}
                  onChange={(e) => setVideoData({...videoData, title: e.target.value})}
                  placeholder="Enter video title"
                />
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Description (Optional)</Label>
                <TextArea 
                  theme={theme}
                  value={videoData.description}
                  onChange={(e) => setVideoData({...videoData, description: e.target.value})}
                  placeholder="Enter video description"
                />
              </FormGroup>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <Button type="button" variant="outline" onClick={() => { setShowVideoModal(false); setVideoData({ video_url: '', title: '', description: '' }); }}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={!videoData.video_url || uploading}>
                  {uploading ? 'Adding...' : 'Add Video'}
                </Button>
              </div>
            </div>
          </form>
        </Modal>
      )}
    </Container>
  );
};

export default AlbumMediaManagement;

